import { useState, useCallback } from 'react';

export interface FiltersState {
  source: string;
  limit: number;
  sortBy: string;
  page: number;
  pageSize: number;
  excludeUpdatedPeriod?: string;
  salaryFrom?: string;
  salaryTo?: string;
  salaryCurrency?: string;
  incomeSpecified?: boolean;
  education: string[];
  cities: string[];
  cityIds?: string[];
  workFormat?: string;
  metroCityId?: string;
  metro?: string | string[];
  onlyRussia?: boolean;
  ageFrom?: string;
  ageTo?: string;
  includeNoAge: boolean;
  gender?: string;
  hasPhoto?: boolean;
  citizenship?: string[];
  stopCompanies: string[];
  donorCompanies: string[];
  companyFilterMode: 'current' | 'all_time' | string;
  vacancyDescription?: string;
  atsVacancyId?: string;
  atsVacancyTitle?: string;
  positionName?: string;
  expectedPositionName?: string;
  experiencePeriod?: string;
  skillsAny: string[];
  skillsAll: string[];
  skillGroups?: Array<{ type: 'any' | 'all'; skills: string[] }>;
  totalExperience?: string;
  positionExperience?: string;
  jobSearchStatus: string[];
  employmentType?: string;
  employmentTypes?: string[];
  searchMode?: 'vacancy' | 'custom';
  industry?: string;
  specialization?: string;
  selectedIndustries?: string[];
  selectedSpecializations?: string[];
  strictSearch?: boolean;
  strictRegionSearch?: boolean;
  excludeJumpers?: boolean;
  hideViewedByCompany?: boolean;
  filterByExpIndustry?: boolean;
  scoringEnabled?: boolean;
  currentCompany?: string;
  previousCompany?: string;
  currentPositionOnly?: boolean;
  hasSalary?: boolean;
  updatedAfter?: string;
  updatedBefore?: string;
  createdAfter?: string;
  createdBefore?: string;
  // REL-21: фильтр "Только мои кандидаты"
  onlyMyCandidates?: boolean;

  // Avito-only поля. Все опциональны — у других источников игнорируются.
  // Имена и значения соответствуют параметрам /job/v1/resumes/ из openapi.yaml.
  // Используем массивы для multi-select (Avito принимает CSV из ID/значений).
  avitoEducationLevel?: string[];          // higher | unfinished-higher | secondary | special-secondary
  avitoSchedule?: string[];                // partial-day | full-day | fly-in-fly-out | flexible | shift | remote
  avitoBusinessTrip?: string[];            // ready | never | sometimes
  avitoRelocation?: string[];              // possible | never
  avitoKeywords?: string[];                // обязательные слова для query через AND
  avitoExcludedWords?: string[];           // исключаемые слова для query через AND -слово
  avitoExperienceMin?: string;             // строкой (как ageFrom/salaryFrom), 0..50
  avitoExperienceMax?: string;
  avitoDriverLicence?: string;             // '' | 'true' | 'false'
  avitoDriverLicenceCategory?: string[];   // a | b | be | c | ce | d | de | m | tm | tb
  avitoDrivingExperience?: string[];       // less-than-three-years | more-than-three-years
  avitoOwnTransport?: string[];            // false | car | cargo-car | bike | scooter
  avitoMedicalBook?: string;               // '' | 'true' | 'false'
  avitoUpdatedFrom?: string;               // RFC3339, выводится из date-инпута
  avitoUpdatedTo?: string;
}

export const defaultFiltersState: FiltersState = {
  source: 'hh',
  limit: 20,
  sortBy: 'relevance',
  page: 1,
  pageSize: 20,
  excludeUpdatedPeriod: '',
  salaryFrom: '',
  salaryTo: '',
  salaryCurrency: 'RUR',
  incomeSpecified: false,
  hasSalary: false,
  education: [],
  cityIds: [],
  cities: [],
  workFormat: '',
  metroCityId: '',
  metro: '',
  ageFrom: '',
  ageTo: '',
  includeNoAge: true,
  gender: '',
  hasPhoto: false,
  citizenship: [],
  stopCompanies: [],
  donorCompanies: [],
  companyFilterMode: 'current',
  vacancyDescription: '',
  atsVacancyId: '',
  atsVacancyTitle: '',
  positionName: '',
  expectedPositionName: '',
  experiencePeriod: '',
  skillsAny: [],
  skillsAll: [],
  skillGroups: undefined,
  totalExperience: '',
  positionExperience: '',
  jobSearchStatus: [],
  employmentType: '',
  employmentTypes: [],
  searchMode: 'custom',
  industry: '',
  specialization: '',
  selectedIndustries: [],
  selectedSpecializations: [],
  strictSearch: false,
  strictRegionSearch: false,
  excludeJumpers: false,
  hideViewedByCompany: false,
  filterByExpIndustry: false,
  scoringEnabled: true,
  updatedAfter: '',
  updatedBefore: '',
  createdAfter: '',
  createdBefore: '',
  // REL-21: по умолчанию выключен
  onlyMyCandidates: false,
  // Avito-дефолты
  avitoEducationLevel: [],
  avitoSchedule: [],
  avitoBusinessTrip: [],
  avitoRelocation: [],
  avitoKeywords: [],
  avitoExcludedWords: [],
  avitoExperienceMin: '',
  avitoExperienceMax: '',
  avitoDriverLicence: '',
  avitoDriverLicenceCategory: [],
  avitoDrivingExperience: [],
  avitoOwnTransport: [],
  avitoMedicalBook: '',
  avitoUpdatedFrom: '',
  avitoUpdatedTo: '',
};

export interface UseFiltersProps {
  initialFilters?: Partial<FiltersState>;
  onChange?: (filters: Partial<FiltersState>) => void;
}

export interface UseFiltersReturn {
  filters: FiltersState;
  setField: (field: keyof FiltersState, value: any) => void;
  updateFilters: (updates: Partial<FiltersState>) => void;
  resetFilters: () => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

function toStringList(value: any): string[] {
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  return list.map(String).filter(Boolean);
}

export function useFilters({
  initialFilters = {},
  onChange
}: UseFiltersProps): UseFiltersReturn {
  const [filters, setFilters] = useState<FiltersState>({
    ...defaultFiltersState,
    ...initialFilters
  });

  const setField = useCallback((field: keyof FiltersState, value: any) => {
    const nextValue = field === 'education' ? toStringList(value) : value;
    const updatedFilters = { ...filters, [field]: nextValue };
    setFilters(updatedFilters);
    onChange?.({ [field]: nextValue } as Partial<FiltersState>);
  }, [filters, onChange]);

  const updateFilters = useCallback((updates: Partial<FiltersState>) => {
    const nextUpdates = { ...updates } as Partial<FiltersState>;
    if ('education' in nextUpdates) {
      (nextUpdates as any).education = toStringList((nextUpdates as any).education);
    }
    const updatedFilters = { ...filters, ...nextUpdates };
    setFilters(updatedFilters);
    onChange?.(nextUpdates);
  }, [filters, onChange]);

  const resetFilters = useCallback(() => {
    setFilters(defaultFiltersState);
    onChange?.(defaultFiltersState);
  }, [onChange]);

  const clearFilters = useCallback(() => {
    setFilters(defaultFiltersState);
    onChange?.(defaultFiltersState);
  }, [onChange]);

  const hasActiveFilters = useCallback(() => {
    return (
      filters.salaryFrom !== '' ||
      filters.salaryTo !== '' ||
      filters.incomeSpecified === true ||
      (Array.isArray(filters.education) && filters.education.length > 0) ||
      filters.cities.length > 0 ||
      (filters.cityIds && filters.cityIds.length > 0) ||
      filters.ageFrom !== '' ||
      filters.ageTo !== '' ||
      !filters.includeNoAge ||
      filters.gender !== '' ||
      filters.hasPhoto === true ||
      (filters.citizenship && filters.citizenship.length > 0) ||
      filters.stopCompanies.length > 0 ||
      filters.donorCompanies.length > 0 ||
      filters.positionName !== '' ||
      filters.expectedPositionName !== '' ||
      !!filters.updatedAfter ||
      !!filters.updatedBefore ||
      !!filters.createdAfter ||
      !!filters.createdBefore ||
      (filters.avitoKeywords && filters.avitoKeywords.length > 0) ||
      (filters.avitoExcludedWords && filters.avitoExcludedWords.length > 0) ||
      filters.experiencePeriod !== '' ||
      filters.skillsAny.length > 0 ||
      filters.skillsAll.length > 0 ||
      (Array.isArray(filters.skillGroups) && filters.skillGroups.length > 0) ||
      filters.totalExperience !== '' ||
      filters.positionExperience !== '' ||
      filters.jobSearchStatus.length > 0 ||
      filters.vacancyDescription !== '' ||
      filters.atsVacancyId !== '' ||
      filters.industry !== '' ||
      (filters.selectedIndustries && filters.selectedIndustries.length > 0) ||
      (filters.employmentTypes && filters.employmentTypes.length > 0) ||
      filters.excludeJumpers === true ||
      filters.hideViewedByCompany === true ||
      filters.filterByExpIndustry === true
    );
  }, [filters]);

  return {
    filters,
    setField,
    updateFilters,
    resetFilters,
    clearFilters,
    hasActiveFilters: hasActiveFilters() || false,
  };
}
